/**
 * The Abby welcome TEXT: the built-in text, and the rules one paragraph must
 * meet (spec 2026-10-23 §5 Tab 2; R18, R19). Pure, so every rule is tested in
 * tests/unit/admin/app-experience-welcome-editor.test.ts.
 *
 * The rules are the SERVER's rules (backend/src/app-experience/tts.ts
 * normalizeParagraph and the publish check): the text is trimmed, then it must
 * not be empty, must have at most WELCOME_PARAGRAPH_MAX_CHARS characters, and
 * must pass the contract's ONE text rule (`findUnsafeTextChar`: no line break,
 * no control or invisible character, no broken character). The page checks
 * them first, so the admin reads the problem next to the paragraph and never a
 * server refusal.
 *
 * The built-in text is imported from its SOURCE file by a relative path, for
 * the reasons written in ./contract.ts (the admin panel is not an npm
 * workspace; the file is dependency-free). It is split exactly as the phone
 * splits it (apps/mobile/components/abby/AbbyWelcomeCardV2.tsx:
 * `script.split('\n\n')`), so "built-in" here means what members see.
 */
import { WELCOME_SCRIPT_EN, WELCOME_SCRIPT_ES } from '../../../../../../packages/shared/src/abby/welcome-script';
import {
  WELCOME_MAX_PARAGRAPHS,
  WELCOME_MIN_PARAGRAPHS,
  WELCOME_PARAGRAPH_MAX_CHARS,
  findUnsafeTextChar,
  type ExperienceLocale,
} from './contract';

export const BUILT_IN_TEXT: Readonly<Record<ExperienceLocale, readonly string[]>> = Object.freeze({
  en: Object.freeze(WELCOME_SCRIPT_EN.split('\n\n')),
  es: Object.freeze(WELCOME_SCRIPT_ES.split('\n\n')),
});

/** A copy of the built-in text that the editor may change. */
export function builtInText(locale: ExperienceLocale): string[] {
  return [...BUILT_IN_TEXT[locale]];
}

/** What the server keeps of each paragraph: the trimmed text. */
export function trimParagraphs(paragraphs: readonly string[]): string[] {
  return paragraphs.map((p) => p.trim());
}

export function sameParagraphs(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((p, i) => p === b[i]);
}

/** The trimmed text equals the phone's built-in split for this language. */
export function isBuiltInText(paragraphs: readonly string[], locale: ExperienceLocale): boolean {
  return sameParagraphs(trimParagraphs(paragraphs), BUILT_IN_TEXT[locale]);
}

/** The characters that count against the limit: the trimmed text. */
export function countedLength(text: string): number {
  return text.trim().length;
}

/** Any C0/C1 control character (newline and tab included): what the editor cleans on paste. */
// eslint-disable-next-line no-control-regex
export const CONTROL_CHARS = /[\u0000-\u001f\u007f-\u009f]/;

/** The first problem of one paragraph, in plain words; null when it is good. `index` counts from 0. */
export function paragraphProblem(text: string, index: number): string | null {
  const n = index + 1;
  const length = countedLength(text);
  if (length === 0) return `Paragraph ${n} is empty. Write text or remove the paragraph.`;
  if (length > WELCOME_PARAGRAPH_MAX_CHARS) {
    return `Paragraph ${n} has ${length} characters. The limit is ${WELCOME_PARAGRAPH_MAX_CHARS}. Make it shorter.`;
  }
  if (findUnsafeTextChar(text.trim()) !== null) return `Paragraph ${n} has a line break or a hidden character. Remove it.`;
  return null;
}

/** Every problem of one language's text, in plain words. Empty = the server will accept it. */
export function textProblems(paragraphs: readonly string[]): string[] {
  if (paragraphs.length < WELCOME_MIN_PARAGRAPHS) {
    return [`Write at least ${WELCOME_MIN_PARAGRAPHS} paragraph.`];
  }
  if (paragraphs.length > WELCOME_MAX_PARAGRAPHS) {
    return [`Use at most ${WELCOME_MAX_PARAGRAPHS} paragraphs. There are ${paragraphs.length} now.`];
  }
  return paragraphs.flatMap((p, i) => {
    const problem = paragraphProblem(p, i);
    return problem ? [problem] : [];
  });
}
